import React from "react";
import {
	Alert,
	AlertTitle,
	Backdrop,
	Box,
	Button,
	Stack,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

const severityAccent = {
	info: "#2563eb",
	success: "#16a34a",
	warning: "#d97706",
	error: "#dc2626",
	danger: "#dc2626",
};

export default function BackdropAlert({
	open,
	onClose,
	severity = "info",
	title,
	message,
	description,
	closeOnBackdropClick = true,
	actions,
	primaryAction,
	secondaryAction,
	maxWidth = 560,
	backdropSx,
}) {
	const handleBackdropClick = () => {
		if (closeOnBackdropClick) {
			onClose?.();
		}
	};

	const handleCardClick = (event) => {
		event.stopPropagation();
	};

	const muiSeverity = severity === "danger" ? "error" : severity;
	const accentColor = severityAccent[severity] || severityAccent[muiSeverity] || severityAccent.info;

	const normalizedActions =
		actions ||
		[secondaryAction, primaryAction].filter(Boolean).map((action, index) => ({
			key: action?.key || `action-${index}`,
			label: action?.label,
			onClick: action?.onClick,
			variant: action?.variant || (index === 1 ? "contained" : "outlined"),
			color: action?.color || "inherit",
			disabled: Boolean(action?.disabled),
			isSecondary: index === 0,
		}));

	const actionsToRender =
		actions?.map((action, index) => ({
			...action,
			isSecondary: action?.variant === "outlined" || index === 1,
		})) || normalizedActions;

	return (
		<Backdrop
			sx={(theme) => ({
				color: "#fff",
				zIndex: theme.zIndex.drawer + 1,
				backgroundColor: alpha("#020617", 0.78),
				px: 2,
				...backdropSx,
			})}
			open={Boolean(open)}
			onClick={handleBackdropClick}
		>
			<Box
				onClick={handleCardClick}
				sx={{
					width: "100%",
					maxWidth,
					"@media (max-width: 600px)": {
						maxWidth: "calc(100vw - 32px)",
					},
				}}
			>
				<Alert
					severity={muiSeverity}
					sx={{
						position: "relative",
						alignItems: "flex-start",
					p: { xs: 2.5, sm: 3.5 },
						overflow: "hidden",
					borderRadius: { xs: 3, sm: 3 },
						bgcolor: "rgba(255, 255, 255, 0.98)",
						border: "1px solid rgba(148, 163, 184, 0.22)",
						boxShadow: "0 28px 80px rgba(2, 6, 23, 0.28)",
						"& .MuiAlert-icon": {
							display: "flex",
							alignItems: "center",
							pt: 0,
							pl: 0,
							mr: 2,
							fontSize: 32,
						},
						"& .MuiAlert-message": {
							width: "100%",
							p: 0,
						},
						"& .MuiAlert-action": {
							display: "none",
						},
					}}
				>
					<Box
						sx={{
							display: "none",
						}}
					/>

					<Box>
						<Box
							sx={{
								display: "inline-flex",
								alignItems: "center",
								px: 1.25,
								py: 0.5,
								borderRadius: 999,
								mb: 1.25,
								fontSize: 12,
								fontWeight: 700,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
								color: accentColor,
								backgroundColor: alpha(accentColor, 0.1),
							}}
						>
							{severity}
						</Box>

						{title ? (
							<AlertTitle sx={{ mb: 0.75, color: "#0f172a", fontSize: { xs: 19, sm: 20 }, fontWeight: 700, lineHeight: 1.2 }}>
								{title}
							</AlertTitle>
						) : null}

						{message ? (
							<Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: "#475569", lineHeight: 1.45 }}>
								{message}
							</Typography>
						) : null}

						{description ? (
							<Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: accentColor, mt: 1, lineHeight: 1.45 }}>
								{description}
							</Typography>
						) : null}

						{actionsToRender.length > 0 ? (
							<Stack
								direction={{ xs: "column", sm: "row" }}
								spacing={1.25}
								sx={{ mt: 2, width: "100%", justifyContent: "flex-end" }}
							>
									{actionsToRender.map((action) => {
										const actionAccent =
											action?.color === "indigo"
												? "#4f46e5"
												: severityAccent[action?.color] || accentColor;

										return (
									<Button
										key={action.key}
											variant={action.isSecondary ? "outlined" : "contained"}
										color="inherit"
										size="medium"
										disabled={action.disabled}
										onClick={action.onClick}
										sx={{
											textTransform: "none",
											borderRadius: 1.25,
											px: 1.75,
											py: 0.8,
											fontSize: { xs: 14, sm: 14 },
											fontWeight: 700,
											width: { xs: "100%", sm: "auto" },
										...(action.isSecondary
											? {
												color: actionAccent,
												borderColor: alpha(actionAccent, 0.55),
												backgroundColor: alpha(actionAccent, 0.08),
														boxShadow: "none",
												"&:hover": {
													borderColor: actionAccent,
													backgroundColor: alpha(actionAccent, 0.14),
												},
											}
											: {}),
										...(action.isSecondary
											? {
												borderWidth: 1,
											}
											: {
												backgroundColor: actionAccent,
												color: "#ffffff",
												"&:hover": {
													backgroundColor: actionAccent,
													filter: "brightness(0.95)",
												},
											}),
												...(action?.sx || {}),
										}}
									>
										{action.label}
									</Button>
										);
									})}
							</Stack>
						) : null}
					</Box>
				</Alert>
			</Box>
		</Backdrop>
	);
}

